import { GhostButton, Spinner, Thermostat } from '@mgh-app/component-library';
import { DateTime } from 'luxon';
import { AwayMode, Schedule } from 'models/Thing';
import { useParams } from 'react-router';
import {
  useGetTemperatureValuesQuery,
  useSetTemperatureValuesMutation,
} from 'services/ThingsService';
import { POLLING_INTERVAL } from 'services/apiConfig';
import {
  display,
  fontWeight,
  gridTemplateColumns,
  gridTemplateRows,
  justifyContent,
  justifyItems,
  margin,
  textAlign,
  twCls,
} from 'style';

const HOUR = 60;

const getNextScheduleInEpoch = (schedule: Schedule, now: DateTime) => {
  const filter = [6, 7].includes(now.weekday) ? '5-6' : '0-4';

  return getNextScheduleInEpochCore(
    Object.fromEntries(Object.entries(schedule).filter(([key]) => key.split(' ')[2] === filter)),
    now,
  );
};

const getCurrentScheduledSetPoint = (now: DateTime, schedule?: Schedule, away?: AwayMode) => {
  if (away?.mode === 'ON') {
    return away.setPoint;
  }

  return schedule && getCurrentScheduleSlotSetPoint(schedule, now);
};

const getNextScheduleInEpochCore = (schedule: Schedule, now: DateTime) => {
  const currentTime = now.minute + now.hour * HOUR;
  const slices = Object.entries(schedule)
    .map(([schedule]) => {
      const cronParts = schedule.split(' ');
      return Number(cronParts[0]) + Number(cronParts[1]) * HOUR;
    })
    .sort((a, b) => a - b)
    .filter((entry) => entry > currentTime);

  const nextSlot = slices[0] ?? 24 * HOUR;

  return DateTime.utc(now.year, now.month, now.day).plus({ minutes: nextSlot }).toUnixInteger();
};

const getCurrentScheduleSlotSetPoint = (schedule: Schedule, now: DateTime) => {
  const currentTime = now.minute + now.hour * HOUR;
  const weekdayFilter = [6, 7].includes(now.weekday) ? '5-6' : '0-4';
  const slices = Object.entries(schedule)
    .map(([schedule]) => schedule.split(' '))
    .filter(([, , d]) => d === weekdayFilter)
    .map(([m, h]) => Number(m) + Number(h) * HOUR)
    .filter((entry) => entry <= currentTime)
    .sort((a, b) => b - a);

  const currentSlot = slices[0];

  return Object.entries(schedule).find(([scheduleKey]) => {
    const cronParts = scheduleKey.split(' ');
    return (
      cronParts[2] === weekdayFilter &&
      currentSlot === Number(cronParts[0]) + Number(cronParts[1]) * HOUR
    );
  })?.[1]?.setPoint;
};

export const ThermostatPage = () => {
  const { temperatureThingId = '' } = useParams();
  const { data: temperatureData, isLoading: isLoadingTemperature } = useGetTemperatureValuesQuery(
    temperatureThingId,
    {
      pollingInterval: POLLING_INTERVAL,
      refetchOnMountOrArgChange: true,
    },
  );
  const [setTemperatureValue] = useSetTemperatureValuesMutation();

  const onSetPointChangeCommitted = (value: number) => {
    if (temperatureData) {
      setTemperatureValue({
        ...temperatureData,
        setPoint: value,
        overrideEndTimestamp:
          temperatureData.schedule &&
          getNextScheduleInEpoch(temperatureData.schedule, DateTime.utc()),
      });
    }
  };

  const onSetPointIncreased = (value: number) => {
    if (temperatureData) {
      setTemperatureValue({
        ...temperatureData,
        setPoint: value,
        overrideEndTimestamp:
          temperatureData.schedule &&
          getNextScheduleInEpoch(temperatureData.schedule, DateTime.utc()),
      });
    }
  };

  const onSetPointDecreased = (value: number) => {
    if (temperatureData) {
      setTemperatureValue({
        ...temperatureData,
        setPoint: value,
        overrideEndTimestamp:
          temperatureData.schedule &&
          getNextScheduleInEpoch(temperatureData.schedule, DateTime.utc()),
      });
    }
  };

  const onResumeSchedule = () => {
    if (temperatureData) {
      setTemperatureValue({
        ...temperatureData,
        setPoint:
          getCurrentScheduledSetPoint(
            DateTime.utc(),
            temperatureData.schedule,
            temperatureData.away,
          ) ?? temperatureData.setPoint,
        overrideEndTimestamp: 0,
      });
    }
  };

  return (
    <>
      {isLoadingTemperature && <Spinner />}
      {!isLoadingTemperature && temperatureData && (
        <div
          className={twCls(
            display('grid'),
            gridTemplateColumns('grid-cols-1'),
            gridTemplateRows('grid-rows-fc-min'),
            justifyItems('justify-items-center'),
            justifyContent('justify-center'),
            margin('mt-6', 'mb-3'),
          )}
        >
          <Thermostat
            temperature={temperatureData.temperature}
            initialSetPoint={temperatureData.setPoint}
            onSetPointChangeCommitted={onSetPointChangeCommitted}
            onSetPointIncreased={onSetPointIncreased}
            onSetPointDecreased={onSetPointDecreased}
          />
          <div>
            {temperatureData.schedule &&
            temperatureData.overrideEndTimestamp &&
            temperatureData.overrideEndTimestamp > DateTime.now().toUTC().toUnixInteger() ? (
              <div>
                <div className={twCls(textAlign('text-center'))}>
                  Until{' '}
                  <span className={twCls(fontWeight('font-bold'))}>
                    {DateTime.fromSeconds(temperatureData.overrideEndTimestamp, {
                      zone: 'UTC',
                    }).toLocaleString(DateTime.TIME_SIMPLE)}
                  </span>
                </div>
                <GhostButton onClick={onResumeSchedule}>Resume Schedule</GhostButton>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </>
  );
};
